import React from 'react';
import { formatCurrency, formatPercent, formatDate } from '../utils/formatters';

function InvestmentTable({ data, yearFilter, showFilter, currentWeekRow }) {
  // Filter data based on year and show filters
  const filteredData = React.useMemo(() => {
    let filtered = data;
    
    // Apply year filter
    if (yearFilter !== 'all') {
      filtered = filtered.filter(row => row.year === parseInt(yearFilter));
    }
    
    // Apply show filter
    if (showFilter === 'monthly') {
      filtered = filtered.filter(row => row.isMonthEnd || row.isYearSummary || row.isYearDivider);
    }
    
    return filtered;
  }, [data, yearFilter, showFilter]);

  if (filteredData.length === 0) {
    return (
      <div className="table-container">
        <div style={{ padding: '20px', textAlign: 'center', color: '#7f8c8d' }}>
          No data available. Please check your filters or wait for data to load.
        </div>
      </div>
    );
  }

  return (
    <div className="table-container">
      <table id="investment-table">
        <thead>
          <tr>
            <th>Week</th>
            <th>Date (Friday)</th>
            <th>Gross Total</th>
            <th>Gross ($)</th>
            <th>Gross (%)</th>
            <th>Net Total</th>
            <th>Net ($)</th>
            <th>Net (%)</th>
            <th>Gains</th>
            <th>Tax ($)</th>
            <th>Tax (%)</th>
            <th>Federal ($)</th>
            <th>City/State ($)</th>
            <th>NIIT ($)</th>
            <th>30% Net</th>
            <th>40% Net</th>
            <th>50% Net</th>
            <th>60% Net</th>
            <th>70% Net</th>
          </tr>
        </thead>
        <tbody id="table-body">
          {filteredData.map((row, index) => {
            if (row.isYearDivider) {
              return (
                <tr key={`divider-${row.year}`} className="year-divider">
                  <td colSpan="19" style={{ textAlign: 'center', fontWeight: 'bold', color: '#7f8c8d' }}>
                    {row.year} Summary: Net Gain {formatCurrency(row.netGain)} ({formatPercent(row.netGainPercent)})
                  </td>
                </tr>
              );
            }

            if (row.isYearSummary) {
              return (
                <tr key={`summary-${row.year}`} className="year-summary">
                  <td>Year {row.year}</td>
                  <td>{formatDate(row.date)}</td>
                  <td>-</td>
                  <td className="currency">{formatCurrency(row.grossGain)}</td>
                  <td>-</td>
                  <td>-</td>
                  <td className="currency">{formatCurrency(row.netGain)}</td>
                  <td className="percentage">{formatPercent(row.netGainPercent)}</td>
                  <td className="currency">{formatCurrency(row.netGain)}</td>
                  <td className="tax">{formatCurrency(row.tax.total)}</td>
                  <td className="percentage">{formatPercent(row.tax.effectiveRate)}</td>
                  <td className="tax">{formatCurrency(row.tax.federal)}</td>
                  <td className="tax">{formatCurrency(row.tax.stateCity)}</td>
                  <td className="tax">{formatCurrency(row.tax.niit)}</td>
                  <td className="thirty-percent-net month-total">{formatCurrency(row.thirtyPercentShare)}</td>
                  <td className="forty-percent-net month-total">{formatCurrency(row.reinvestmentShare)}</td>
                  <td className="fifty-percent-net month-total">{formatCurrency(row.fiftyPercentShare)}</td>
                  <td className="sixty-percent-net month-total">{formatCurrency(row.yourMonthlyShare)}</td>
                  <td className="seventy-percent-net month-total">{formatCurrency(row.seventyPercentShare)}</td>
                </tr>
              );
            }

            // Regular weekly data row
            const isCurrentWeek = currentWeekRow && 
              currentWeekRow.week === row.week && 
              currentWeekRow.year === row.year;
            
            const rowClasses = [
              isCurrentWeek ? 'current-week' : '',
              row.isTaxPaymentWeek ? 'tax-payment-week' : '',
              row.milestone ? 'milestone' : ''
            ].filter(Boolean).join(' ');

            return (
              <tr 
                key={`week-${row.week}-${row.year}`}
                id={`week-${row.week}-${row.year}`}
                className={rowClasses}
              >
                <td>{row.week}</td>
                <td>{formatDate(row.date)}</td>
                <td className="currency">{formatCurrency(row.cumulativeGross)}</td>
                <td className="currency">{formatCurrency(row.grossGain)}</td>
                <td className="percentage">{formatPercent(row.grossGainPercent)}</td>
                <td className="currency">{formatCurrency(row.cumulativeNet)}</td>
                <td className="currency">{formatCurrency(row.netGain)}</td>
                <td className="percentage">{formatPercent(row.netGainPercent)}</td>
                <td className="currency">{formatCurrency(row.yearlyNetGains)}</td>
                <td className="tax">{formatCurrency(row.tax.total)}</td>
                <td className="percentage">{formatPercent(row.tax.effectiveRate)}</td>
                <td className="tax">{formatCurrency(row.tax.federal)}</td>
                <td className="tax">{formatCurrency(row.tax.stateCity)}</td>
                <td className="tax">{formatCurrency(row.tax.niit)}</td>
                <td className={`thirty-percent-net${row.isMonthEnd ? ' month-total' : ''}`}>
                  {formatCurrency(row.thirtyPercentShare)}
                </td>
                <td className={`forty-percent-net${row.isMonthEnd ? ' month-total' : ''}`}>
                  {formatCurrency(row.reinvestmentShare)}
                </td>
                <td className={`fifty-percent-net${row.isMonthEnd ? ' month-total' : ''}`}>
                  {formatCurrency(row.fiftyPercentShare)}
                </td>
                <td className={`sixty-percent-net${row.isMonthEnd ? ' month-total' : ''}`}>
                  {formatCurrency(row.yourMonthlyShare)}
                </td>
                <td className={`seventy-percent-net${row.isMonthEnd ? ' month-total' : ''}`}>
                  {formatCurrency(row.seventyPercentShare)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default InvestmentTable;
